/**
 * useFeatureFlag Hook
 *
 * GW-918: React hook for client-side feature flag checking.
 * Fetches flag status from API, caches results in featureFlagsSlice,
 * and supports loading and error states.
 *
 * GW-4687: Removed clearFeatureFlags on org change — it caused a cascade
 * where 3 sidebar hooks independently re-fetched, each triggering a store
 * update and re-render (visible as sidebar stutter). Instead, flags are
 * re-fetched lazily via TTL expiry or eagerly via fetchAllKnownFlags in App.tsx.
 *
 * GW-10446: The fetch-triggering effect used to gate on `isAuthenticated &&
 * currentOrg?.id` (GW-6672) because flags were read from an authenticated
 * gw-admin-api endpoint that required an X-Organization-Id header. GW-8339
 * replaced that endpoint with a synchronous, deploy-time-injected static read
 * (window.__FEATURE_FLAGS__ via getStaticFeatureFlag) that needs neither auth
 * nor org context — but the gate was never removed. That left a real gap: an
 * authenticated user's `currentOrg` only populates once fetchUserOrganizations
 * resolves (an async call chained after signIn), so any component reading a
 * flag before that resolves got a stuck `isEnabled: false` — with no fetch
 * ever firing to correct it, since the gate blocked it — until currentOrg
 * populated. SearchNextPage's `missionsEnabled` check hit exactly this
 * window, making "Go to Confirm" a silent no-op for a freshly-authenticated
 * user. The flag read no longer depends on org context, so neither does this
 * effect.
 */

import { useEffect, useRef } from 'react';

import { useFeatureFlagsStore } from '@/stores/store';

import type { KnownFeatureFlags } from '@gw/ui/types';

/** Flags older than this are considered stale and will be re-fetched */
const FLAG_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface UseFeatureFlagResult {
  /** Whether the feature flag is enabled for the current user context */
  isEnabled: boolean;
  /** Whether the flag is currently being fetched */
  isLoading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Whether the flag has been fetched (regardless of success/failure) */
  hasLoaded: boolean;
}

/**
 * Hook to check if a feature flag is enabled for the current user context.
 *
 * @param flagName - The name of the feature flag to check
 * @returns Object containing isEnabled, isLoading, error, and hasLoaded states
 *
 * @example
 * ```tsx
 * const { isEnabled, isLoading } = useFeatureFlag('datasets_marketplace');
 *
 * if (isLoading) return <Spinner />;
 * if (!isEnabled) return null;
 * return <DatasetsUI />;
 * ```
 */
export function useFeatureFlag(flagName: KnownFeatureFlags): UseFeatureFlagResult {
  const { featureFlags, isLoading, hasLoaded, lastLoaded, errors, batchLoading, fetchFeatureFlag } =
    useFeatureFlagsStore();

  const operationKey = `fetchFeatureFlag:${flagName}`;
  const flagData = featureFlags[flagName];
  const hasBeenLoaded = hasLoaded[operationKey] ?? false;
  const isCurrentlyLoading = (isLoading[operationKey] ?? false) || (batchLoading && !hasBeenLoaded);
  const error = errors[operationKey] ?? null;
  const loadedAt = lastLoaded?.[operationKey] ?? 0;

  // Track whether we've already triggered a fetch for this flag in this component instance
  // to avoid re-triggering on every render cycle while the fetch is in-flight.
  const fetchTriggeredRef = useRef(false);

  // Reset fetch trigger when the flag becomes loaded (so a future TTL expiry can re-fetch)
  useEffect(() => {
    if (hasBeenLoaded) {
      fetchTriggeredRef.current = false;
    }
  }, [hasBeenLoaded]);

  // Fetch feature flag on mount if not cached or if stale (TTL expired).
  // Skip individual fetch while batch loading is in-flight.
  // GW-10446: no auth/org gate — getStaticFeatureFlag (GW-8339) reads the
  // deploy-time-injected window.__FEATURE_FLAGS__ synchronously, with no
  // network call and no org context required. Gating this on currentOrg
  // (the pre-GW-8339 shape, when flags came from an authenticated
  // gw-admin-api endpoint) left the flag stuck unfetched — and therefore
  // `isEnabled` stuck false — for every render between sign-in and
  // fetchUserOrganizations resolving.
  useEffect(() => {
    if (batchLoading || isCurrentlyLoading || fetchTriggeredRef.current) {
      return;
    }

    const isStale = hasBeenLoaded && loadedAt > 0 && Date.now() - loadedAt > FLAG_TTL_MS;

    if (!hasBeenLoaded || isStale) {
      fetchTriggeredRef.current = true;
      fetchFeatureFlag(flagName);
    }
  }, [flagName, hasBeenLoaded, isCurrentlyLoading, batchLoading, loadedAt, fetchFeatureFlag]);

  return {
    isEnabled: flagData?.enabled ?? false,
    isLoading: isCurrentlyLoading,
    error,
    hasLoaded: hasBeenLoaded,
  };
}
